import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { UserOut } from "../api/types";

/**
 * The avatar menu (docs/15 §1): who you are signed in as, and the things that belong to the
 * person rather than to a product — your account, security, and signing out. Products live in
 * the launcher beside it.
 */
export function AccountMenu({
  me,
  onConsole,
}: {
  me: UserOut | undefined;
  onConsole: boolean;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const initial = (me?.first_name || me?.email || "?").charAt(0).toUpperCase();

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const item =
    "block rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100";

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        aria-label="Your account"
        aria-expanded={open}
        title={me?.email}
        onClick={() => setOpen((was) => !was)}
        className={`flex h-9 w-9 items-center justify-center rounded-full bg-brand text-sm font-semibold text-white ${
          onConsole ? "ring-2 ring-white/30" : ""
        }`}
      >
        {initial}
      </button>
      {open ? (
        <div className="absolute right-0 z-20 mt-2 w-60 rounded-lg border border-slate-200 bg-white p-2 shadow-card-lg">
          <div className="px-3 py-2">
            <p className="text-sm font-semibold text-slate-900">
              {me?.first_name} {me?.last_name ?? ""}
            </p>
            <p className="truncate text-xs text-slate-500">{me?.email}</p>
          </div>
          <Link
            className={`${item} text-accent-dark`}
            to="/logout"
            onClick={() => setOpen(false)}
          >
            Sign out
          </Link>
        </div>
      ) : null}
    </div>
  );
}
